import React, { useMemo, useState } from "react";
import { LayoutGrid, List, Calendar, MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const matches = (e, f) =>
  (f.type === "all" || e.type === f.type) && (!f.from || (e.date || "") >= f.from) && (!f.to || (e.date || "").slice(0, 10) <= f.to) &&
  (!f.q || `${e.title} ${e.location || ""} ${e.type || ""}`.toLowerCase().includes(f.q.toLowerCase()));

function ViewToggle({ view, onChange }) {
  const btn = (v, Icon, label) => (
    <button onClick={() => onChange(v)} className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold ${view === v ? "bg-crimson text-parchment" : "text-crimson hover:bg-gold-light"}`} aria-pressed={view === v} data-testid={`events-view-${v}`}><Icon size={14} /> {label}</button>
  );
  return <div className="ml-auto flex rounded-lg border border-gold/40 p-0.5">{btn("cards", LayoutGrid, "Cards")}{btn("list", List, "List")}</div>;
}

function EventFilters({ f, setF, types }) {
  return (
    <>
      <Input placeholder="Search events…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="w-56" data-testid="events-search" />
      <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
        <SelectTrigger className="w-40" data-testid="events-type"><SelectValue placeholder="All types" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All types</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="w-40" title="From" data-testid="events-from" />
      <Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="w-40" title="To" data-testid="events-to" />
    </>
  );
}

function EventList({ shown, onOpen }) {
  return (
    <div className="glass divide-y divide-gold/15 rounded-2xl" data-testid="events-list">
      {shown.map((e) => (
        <button key={e.id} onClick={() => onOpen(e)} className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-gold-light/50" data-testid={`event-row-${e.id}`}>
          <span className="w-28 shrink-0 font-mono-accent text-sm text-palm"><Calendar size={13} className="mr-1 inline" />{(e.date || "").slice(0, 10)}</span>
          <span className="min-w-0 flex-1 truncate font-semibold text-crimson">{e.title}</span>
          <span className="eyebrow text-gold">{String(e.type || "").replace(/_/g, " ")}</span>
          {e.location && <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex"><MapPin size={12} /> {e.location}</span>}
        </button>
      ))}
      {shown.length === 0 && <p className="p-4 text-sm text-muted-foreground">No events match.</p>}
    </div>
  );
}

// List/card toggle + date-range/type filters for member events. View preference only in localStorage.
export function EventsBrowser({ events, render }) {
  const [view, setView] = useState(() => localStorage.getItem("events-view") || "cards");
  const setV = (v) => { setView(v); localStorage.setItem("events-view", v); };
  const [f, setF] = useState({ q: "", type: "all", from: "", to: "" });
  const [openEv, setOpenEv] = useState(null);
  const types = useMemo(() => [...new Set(events.map((e) => e.type).filter(Boolean))].sort(), [events]);
  const shown = useMemo(() => events.filter((e) => matches(e, f)), [events, f]);
  return (
    <div data-testid="events-browser">
      <div className="mb-4 flex flex-wrap items-end gap-2"><EventFilters f={f} setF={setF} types={types} /><ViewToggle view={view} onChange={setV} /></div>
      <p className="mb-3 text-xs text-muted-foreground" data-testid="events-count">{shown.length} event{shown.length === 1 ? "" : "s"}</p>
      {view === "cards" ? <div className="grid gap-5 md:grid-cols-2">{shown.map(render)}</div> : <EventList shown={shown} onOpen={setOpenEv} />}
      <Dialog open={!!openEv} onOpenChange={(o) => !o && setOpenEv(null)}>
        <DialogContent className="max-w-lg" data-testid="event-detail-dialog"><DialogHeader><DialogTitle className="sr-only">Event</DialogTitle></DialogHeader>{openEv && render(openEv)}</DialogContent>
      </Dialog>
    </div>
  );
}
